import React, { useEffect, useRef, useState } from 'react';
import { playClick } from '../../../utils/sounds';
import './experience.css';

const EXPERIENCES = [
  {
    id: 'dsel',
    company: 'David Schaffers Engineering Ltd. (DSEL)',
    role: 'Intermediate Designer',
    location: 'Markham, ON (Remote)',
    period: 'May 2025 \u2014 Present',
    current: true,
    bullets: [
      'Completed detailed civil designs for large-scale residential and masterplan communities, including grading, servicing, erosion and sediment control, and stormwater management, supporting projects through municipal approval processes.',
      'Optimized site layouts by integrating topography, environmental constraints, geotechnical conditions, hydrogeological considerations, and municipal servicing requirements to balance constructability, cost efficiency, and regulatory compliance.',
      'Supported high-volume submissions under accelerated 10-day subdivision approval timelines, managing frequent resubmissions and evolving municipal requirements while maintaining technical quality.',
    ],
    links: [
      { label: 'dsel.ca', url: 'https://dsel.ca/' },
    ],
  },
  {
    id: 'bbrealty',
    company: 'B&B Realty N\u2019 Renos',
    role: 'System Administrator',
    location: 'Caledon, ON',
    period: '2019 \u2014 Present',
    current: true,
    bullets: [
      'Co-founded a home renovation and real estate services company, responsible for design work including drafting renovation layouts, kitchen designs, and interior concepts.',
      'Support real estate analysis including property assessments, comparable market evaluations, and renovation feasibility to guide investment decisions.',
    ],
    links: [
      { label: 'bbrealtyrenos.com', url: 'https://www.bbrealtyrenos.com/' },
    ],
  },
  // HIDDEN — uncomment to re-add B&B TechnoSolutions
  // {
  //   id: 'bbtechno',
  //   company: 'B&B TechnoSolutions',
  //   role: 'Co-Founder',
  //   location: 'Toronto, ON',
  //   period: '2019 \u2014 Present',
  //   current: true,
  //   bullets: [
  //     'Co-founded B&B TechnoSolutions, developing and sourcing critical components including fasteners, axle shafts, gears, and custom tooling.',
  //     'Integrated domestic and global family-run supply chains to effectively serve key clients such as Linamar and Exco.',
  //     'Manage order logistics, 3D design, and business operations across multiple ventures.',
  //   ],
  //   links: [
  //     { label: 'bbtechnosol.com', url: 'https://bbtechnosol.com/' },
  //   ],
  // },
  {
    id: 'crozier',
    company: 'Crozier: Consulting Engineers',
    role: 'Engineer-in-Training',
    location: 'Guelph & Bradford, ON (Hybrid)',
    period: 'Jan 2022 \u2014 May 2025',
    current: false,
    bullets: [
      'Supported civil projects from early design through construction, contributing to site feasibility, grading, servicing (municipal and private), erosion and sediment control, and stormwater management for residential, commercial, and industrial developments through municipal approval processes.',
      'Prepared and coordinated more than 25 technical deliverables, including Feasibility Study Reports, Stormwater Management Reports, Environmental Compliance Approvals, and due diligence memoranda.',
      'Supported site layout and grading optimization by integrating topography, environmental constraints, permitting requirements, and constructability considerations to meet cost, performance, and regulatory requirements.',
      'Provided construction-phase civil support through more than 20 field visits, coordinating with contractors, responding to RFIs, verifying site conditions, performing quantity take-offs, and resolving field-to-design issues.',
      'Contributed to project development by supporting more than 20 proposals with pricing, budgeting, and scheduling input, and assisting with internal initiatives to improve engineering and project delivery workflows.',
    ],
    links: [
      { label: 'cfcrozier.ca', url: 'https://www.cfcrozier.ca/' },
    ],
  },
  {
    id: 'exco',
    company: 'Exco Technologies Ltd.',
    role: 'CAD Programmer (Co-op)',
    location: 'Markham, ON (Hybrid)',
    period: 'May 2021 \u2014 Aug 2021',
    current: false,
    bullets: [
      'Reviewed draft drawings of extrusion dies, identified discrepancies, and reported findings to management.',
      'Managed Excel database of over 150 customer CAD orders, verifying quality and dimensions for accuracy and organization.',
      'Improved the manual data collection process for over 70 unique extrusion dies by automating data entry and analysis using custom-built Python and AutoLISP scripts, tracking 30+ key metrics for die performance.',
    ],
    links: [
      { label: 'excocorp.com', url: 'https://www.excocorp.com/' },
    ],
  },
];

export const Experience = () => {
  const [visible, setVisible] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.03 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const toggle = (id) => {
    playClick();
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <section
      id="Experience"
      className={`experience-section ${visible ? 'visible' : ''}`}
      ref={ref}
      aria-label="Work Experience"
    >
      <div className="container">
        <div className="section-header">
          <h2>EXPERIENCE</h2>
        </div>

        <div className="exp-timeline">
          {EXPERIENCES.map((exp, idx) => (
            <article
              key={exp.id}
              className="exp-entry"
              style={{ animationDelay: `${idx * 0.12}s` }}
            >
              {/* Timeline connector */}
              <div className="tl-track">
                <div className={`tl-dot ${exp.current ? 'current' : ''}`}></div>
                {idx < EXPERIENCES.length - 1 && <div className="tl-line"></div>}
              </div>

              {/* Card */}
              <div className="terminal-window exp-card">
                <div
                  className="terminal-header exp-header-click"
                  onClick={() => toggle(exp.id)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={!collapsed[exp.id]}
                  onKeyDown={(e) => e.key === 'Enter' && toggle(exp.id)}
                >
                  <div className="dots">
                    <span className={`dot ${exp.current ? 'active' : ''}`}></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </div>
                  <span>
                    {exp.company.toLowerCase().replace(/[^a-z0-9]+/g, '_')}.log
                  </span>
                </div>

                <div className="terminal-body">
                  <h3 className="exp-company">{exp.company}</h3>
                  <div className="exp-role">{exp.role}</div>
                  <div className="exp-meta-row">
                    <span className="exp-location">{'\u25C6'} {exp.location}</span>
                    <span className="exp-period">{'\u25C6'} {exp.period}</span>
                  </div>

                  <div className={`exp-bullets ${collapsed[exp.id] ? 'hidden' : ''}`}>
                    {exp.bullets.map((b, i) => (
                      <div key={i} className="exp-bullet">
                        <span className="b-marker">{'\u25B8'}</span>
                        <span>{b}</span>
                      </div>
                    ))}
                  </div>

                  {exp.links && (
                    <div className="exp-links">
                      {exp.links.map((lnk, i) => (
                        <a
                          key={i}
                          href={lnk.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="crt-button amber"
                        >
                          {lnk.label} {'\u2192'}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
